package com.arenaproplay.admin.ui.components.forms

import androidx.compose.runtime.Composable
import com.varabyte.kobweb.compose.css.Cursor
import com.varabyte.kobweb.compose.foundation.layout.Box
import com.varabyte.kobweb.compose.foundation.layout.Column
import com.varabyte.kobweb.compose.foundation.layout.Row
import com.varabyte.kobweb.compose.ui.Alignment
import com.varabyte.kobweb.compose.ui.Modifier
import com.varabyte.kobweb.compose.ui.modifiers.*
import com.varabyte.kobweb.compose.ui.styleModifier
import com.varabyte.kobweb.silk.components.text.SpanText
import com.arenaproplay.admin.domain.admin.SportOption
import com.arenaproplay.admin.ui.theme.bodyMRegular
import com.arenaproplay.admin.ui.theme.bodySMedium
import com.arenaproplay.admin.ui.utils.sBorderRadius
import org.jetbrains.compose.web.attributes.InputType
import org.jetbrains.compose.web.css.*
import org.jetbrains.compose.web.dom.Input
import org.jetbrains.compose.web.dom.Option
import org.jetbrains.compose.web.dom.Select
import org.jetbrains.compose.web.dom.Text

const val OTHER_SPORT = "OTHER"

val CLUB_AMENITIES = listOf(
    "PARKING" to "Parking",
    "CAFE" to "Cafe",
    "WIFI" to "WiFi",
    "RECEPTION" to "Reception",
    "SHOWER" to "Shower",
    "FIRST_AID" to "First aid",
)

/** Parses "4.5" (also tolerates "4,5"); null when not a number. */
fun parseRatingInput(text: String): Double? = text.trim().replace(',', '.').toDoubleOrNull()

/** One weekday's opening hours in the schedule editor. */
data class DayHours(
    val dayOfWeek: Int,
    val open: String,
    val close: String,
    val closed: Boolean,
)

fun defaultWeekHours(): List<DayHours> = (1..7).map { DayHours(it, "08:00", "22:00", false) }

private val DAY_LABELS = listOf("Mon", "Tue", "Wed", "Thu", "Fri", "Sat", "Sun")

@Composable
fun WorkingHoursEditor(week: List<DayHours>, onChange: (List<DayHours>) -> Unit) {
    Column(Modifier.fillMaxWidth()) {
        week.sortedBy { it.dayOfWeek }.forEach { day ->
            Row(
                Modifier.fillMaxWidth().padding(topBottom = 5.px),
                verticalAlignment = Alignment.CenterVertically,
            ) {
                Box(Modifier.width(44.px)) {
                    SpanText(
                        DAY_LABELS[day.dayOfWeek - 1],
                        Modifier.bodySMedium().color(Color("#5D6472")),
                    )
                }
                if (!day.closed) {
                    TimeInput(day.open) { v ->
                        onChange(week.map { if (it.dayOfWeek == day.dayOfWeek) it.copy(open = v) else it })
                    }
                    SpanText("–", Modifier.bodyMRegular().color(Color("#5D6472")).margin(leftRight = 8.px))
                    TimeInput(day.close) { v ->
                        onChange(week.map { if (it.dayOfWeek == day.dayOfWeek) it.copy(close = v) else it })
                    }
                } else {
                    SpanText("Closed all day", Modifier.bodySMedium().color(Color("#8A90A0")))
                }
                Box(Modifier.weight(1f))
                ClosedToggle(day.closed) {
                    onChange(week.map { if (it.dayOfWeek == day.dayOfWeek) it.copy(closed = !day.closed) else it })
                }
            }
        }
    }
}

@Composable
private fun TimeInput(value: String, onChange: (String) -> Unit) {
    Input(InputType.Time) {
        value(value)
        onInput { it.value?.takeIf { v -> v.isNotBlank() }?.let(onChange) }
        style {
            padding(7.px, 10.px)
            property("border-radius", "8px")
            property("border", "1px solid rgba(15,18,34,0.10)")
            property("font-family", "inherit")
            fontSize(14.px)
            property("background-color", "#FFFFFF")
            property("color", "#0F1222")
            property("outline", "none")
        }
    }
}

@Composable
private fun ClosedToggle(closed: Boolean, onToggle: () -> Unit) {
    Box(
        Modifier
            .sBorderRadius(999)
            .padding(leftRight = 12.px, topBottom = 5.px)
            .cursor(Cursor.Pointer)
            .styleModifier {
                if (closed) {
                    property("background", "#FBEDEC")
                    property("border", "1px solid rgba(192,57,43,0.18)")
                } else {
                    property("background", "#FFFFFF")
                    property("border", "1px solid rgba(15,18,34,0.10)")
                }
            }
            .onClick { onToggle() },
    ) {
        SpanText(
            if (closed) "Closed" else "Open",
            Modifier.bodySMedium().color(Color(if (closed) "#D05C46" else "#0BAB64"))
                .styleModifier { property("font-weight", "600") },
        )
    }
}

@Composable
fun AmenityChips(selected: Set<String>, enabled: Boolean, onToggle: (String) -> Unit) {
    Row(
        Modifier.fillMaxWidth().styleModifier {
            property("flex-wrap", "wrap")
            property("gap", "10px")
        }
    ) {
        CLUB_AMENITIES.forEach { (type, label) ->
            val active = type in selected
            Box(
                Modifier
                    .sBorderRadius(999)
                    .padding(leftRight = 16.px, topBottom = 8.px)
                    .cursor(if (enabled) Cursor.Pointer else Cursor.NotAllowed)
                    .styleModifier {
                        if (active) {
                            property("background", "#0F1222")
                        } else {
                            property("background", "#FFFFFF")
                            property("border", "1px solid rgba(15,18,34,0.10)")
                        }
                        property("transition", "background 140ms ease")
                    }
                    .onClick { if (enabled) onToggle(type) },
            ) {
                SpanText(
                    label,
                    Modifier.bodySMedium()
                        .color(Color(if (active) "#FFFFFF" else "#0F1222"))
                        .styleModifier { property("font-weight", "600") },
                )
            }
        }
    }
}

@Composable
fun SportTypeSelect(
    sports: List<SportOption>,
    selected: String,
    label: String = "Type",
    onSelect: (String) -> Unit,
) {
    Column(Modifier.fillMaxWidth()) {
        SpanText(
            label,
            Modifier.fontSize(14.px).color(Color("#5D6472")).margin(bottom = 8.px)
        )
        Select({
            style {
                width(100.percent)
                padding(12.px)
                property("border-radius", "8px")
                fontSize(16.px)
                property("outline", "none")
                property("box-sizing", "border-box")
                property("font-family", "inherit")
                property("background-color", "#FFFFFF")
                property("color", "#0F1222")
                property("border", "1px solid rgba(15,18,34,0.10)")
                property("cursor", "pointer")
            }
            onChange { onSelect(it.value ?: "") }
        }) {
            Option("", { if (selected.isEmpty()) attr("selected", "") }) { Text("Select sport…") }
            sports.filter { it.name.uppercase() != OTHER_SPORT }.forEach { sport ->
                val value = sport.name.uppercase()
                Option(value, { if (selected == value) attr("selected", "") }) { Text(sport.name) }
            }
            Option(OTHER_SPORT, { if (selected == OTHER_SPORT) attr("selected", "") }) { Text("Other") }
        }
    }
}

/** Small green hint under an address field showing the resolved coordinates. */
@Composable
fun CoordinatesHint(latitude: Double?, longitude: Double?) {
    if (latitude != null && longitude != null) {
        Box(Modifier.height(6.px))
        SpanText(
            "📍 ${latitude.asFixed(6)}, ${longitude.asFixed(6)}",
            Modifier.bodySMedium().color(Color("#0BAB64")),
        )
    }
}

private fun Double.asFixed(digits: Int): String = this.asDynamic().toFixed(digits) as String

@Composable
fun FormButton(
    label: String,
    enabled: Boolean,
    primary: Boolean,
    onClick: () -> Unit,
) {
    Box(
        Modifier
            .sBorderRadius(999)
            .padding(leftRight = 18.px, topBottom = 9.px)
            .cursor(if (enabled) Cursor.Pointer else Cursor.NotAllowed)
            .styleModifier {
                if (primary) {
                    property("background", "#0F1222")
                    property("color", "#FFFFFF")
                } else {
                    property("background", "#FFFFFF")
                    property("border", "1px solid rgba(15,18,34,0.10)")
                }
                if (!enabled) property("opacity", "0.5")
            }
            .onClick { if (enabled) onClick() },
    ) {
        SpanText(
            label,
            Modifier.bodySMedium()
                .color(Color(if (primary) "#FFFFFF" else "#0F1222"))
                .styleModifier { property("font-weight", "600") },
        )
    }
}
